import { ReactComponent as Ic3Points } from '@assets/components/panel/ic-3points-h18.svg';
import { IcConfirmedBold, IcGroup } from '@assets/icons/24x24';
import { Box, Center, Checkmark, For, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import useListFormatter from '@hooks/useListFormatter';
import { PlatformSelectors } from '@redux/config/config.selectors';

import { NormalizedGroup } from '@/types/authent/groups.types';

type Props = {
  group: NormalizedGroup;
  isSelected: boolean;
  isActiveGroup: boolean;
  handleChange: (group: NormalizedGroup) => void;
};

const MAX_PLATFORM = 4;

export default function GroupCard({ group, isSelected, isActiveGroup, handleChange }: Readonly<Props>) {
  const formatList = useListFormatter();
  const platforms = useAppSelector(PlatformSelectors.selectAllPlatforms);
  const groupPlatforms = platforms.filter((platform) => group.platforms.includes(platform.code));

  return (
    <VStack
      gap={2}
      width="334px"
      height="234px"
      cursor="pointer"
      alignItems="start"
      backgroundColor={isSelected ? 'blue.800' : 'neutral.800'}
      padding={3}
      className="group"
      clipPath="polygon(0 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 0)"
      _hover={{ backgroundColor: isSelected ? 'blue.800' : 'neutral.600' }}
      onClick={() => handleChange(group)}
    >
      <HStack width="100%">
        <Checkmark
          checked={isSelected}
          height="24px"
          width="24px"
          _groupHover={{
            borderColor: 'skyblue',
          }}
          _hover={{
            color: 'neutral.white',
          }}
        >
          <Icon as={IcConfirmedBold} width="24px" height="24px" />
        </Checkmark>
        {isActiveGroup && <Icon as={IcGroup} boxSize="24px" color="blue.500" marginLeft="auto" />}
      </HStack>
      <Text
        color={isSelected ? 'neutral.white' : 'neutral.200'}
        textStyle="xl"
        _groupHover={{
          color: isSelected ? 'neutral.white' : 'neutral.300',
        }}
      >
        {group.name}
      </Text>
      {groupPlatforms.length > 0 ? (
        <HStack width="100%" gap={0.5}>
          <For each={groupPlatforms.slice(0, MAX_PLATFORM)}>
            {(platform) => (
              <Tooltip key={platform.code} content={platform.name}>
                <Text
                  padding={1}
                  backgroundColor="neutral.900"
                  textTransform="uppercase"
                  lineHeight="18px"
                  userSelect="none"
                  _groupHover={{
                    color: isSelected ? 'neutral.white' : 'neutral.300',
                  }}
                >
                  {platform.name.slice(0, 4)}
                </Text>
              </Tooltip>
            )}
          </For>
          {groupPlatforms.length > MAX_PLATFORM && (
            <Tooltip content={formatList(groupPlatforms.slice(MAX_PLATFORM).map((platform) => platform.name))}>
              <Center padding={1} backgroundColor="neutral.900">
                <Icon as={Ic3Points} color="neutral.300" />
              </Center>
            </Tooltip>
          )}
        </HStack>
      ) : (
        <Box height="34px" />
      )}
      <Text color="neutral.200">{group.profileName ?? ''}</Text>
    </VStack>
  );
}
